/**
 * Packing boards' arithmetic (alignment map V1-F7-01a Allocate, V1-F7-04n PackLotRecord,
 * V1-F7-08k DispatchRecord): planned pouches, cartons, the pack lot's counter gap and loss, and
 * the words for pack-lot, lab and packability states. Pure, integer grams.
 */
import type { Tone } from './payoutBoard'

/** 312400 g → "312.40 kg" (the boards show kilos to 2 decimals, rounded half up). */
export function kg2(grams: number | null | undefined): string {
  if (grams === null || grams === undefined || !Number.isFinite(grams)) return '—'
  const neg = grams < 0
  const tens = Math.round(Math.abs(grams) / 10)
  const whole = Math.floor(tens / 100)
  const frac = String(tens % 100).padStart(2, '0')
  return `${neg ? '−' : ''}${whole.toLocaleString('en-IN')}.${frac} kg`
}

/** Planned pouches = kilos × 1000 ÷ pouch grams, rounded down (F7.1). */
export function plannedPouches(availableG: number, netG: number): number {
  if (!(netG > 0) || !(availableG > 0)) return 0
  return Math.floor(availableG / netG)
}

/** "30 full + 1 of 12" (cartons of `perCarton`). */
export function cartonsText(pouches: number | null, perCarton: number | null): string {
  if (pouches === null || pouches <= 0) return '—'
  if (!perCarton || perCarton <= 0) return `${pouches} pouches, no carton size`
  const full = Math.floor(pouches / perCarton)
  const rest = pouches % perCarton
  if (rest === 0) return `${full} full`
  return full > 0 ? `${full} full + 1 of ${rest}` : `1 of ${rest}`
}
export function cartonCount(pouches: number | null, perCarton: number | null): number | null {
  if (pouches === null || !perCarton || perCarton <= 0) return null
  return Math.ceil(pouches / perCarton)
}

export interface PackLotFigureInput {
  pouchCount: number | null
  netG: number
  labelsPrinted: number | null
  /** Sack kilos scanned into the hopper (run inputs). */
  inputG: number | null
  /** Every output kilo of the run (pouches + leftover sack). */
  outputG: number | null
  /** The pack unit's own kilos (pouches). */
  productG: number | null
  lossLimitPct: number | null
}

export interface PackLotFigures {
  /** Pouches × pouch grams (V1: no spot weights, so overfill shows as loss). */
  packedG: number | null
  leftoverG: number | null
  /** In − packed − leftover; negative = more out than in (a surplus, which holds the lot). */
  lossG: number | null
  /** Loss as % of the kilos in, 2 decimals. */
  lossPct: number | null
  aboveLimit: boolean
  surplus: boolean
  codesPrinted: number | null
  /** Codes printed − pouches: information only in V1 (spoiled and test prints are not typed). */
  counterGap: number | null
}

export function packLotFigures(i: PackLotFigureInput): PackLotFigures {
  const packedG = i.pouchCount !== null ? i.pouchCount * i.netG : i.productG
  const leftoverG =
    i.outputG !== null && i.productG !== null ? Math.max(0, i.outputG - i.productG) : null
  const lossG = i.inputG !== null && packedG !== null ? i.inputG - packedG - (leftoverG ?? 0) : null
  const lossPct = lossG !== null && i.inputG ? Math.round((lossG * 10000) / i.inputG) / 100 : null
  return {
    packedG,
    leftoverG,
    lossG,
    lossPct,
    aboveLimit: lossPct !== null && i.lossLimitPct !== null && lossPct > i.lossLimitPct,
    surplus: lossG !== null && lossG < 0,
    codesPrinted: i.labelsPrinted,
    counterGap:
      i.labelsPrinted !== null && i.pouchCount !== null ? i.labelsPrinted - i.pouchCount : null,
  }
}

/** "0.78%" */
export const pct = (v: number | null | undefined, digits = 2) =>
  v === null || v === undefined ? '—' : `${v.toFixed(digits)}%`

/** The pack lot's state in the boards' words. */
export function packLotState(status: string, hold: string): { tone: Tone; text: string } {
  if (hold === 'RECALLED') return { tone: 'bad', text: 'Recalled' }
  if (hold === 'HELD') return { tone: 'bad', text: 'On hold' }
  switch (status) {
    case 'PACKING':
      return { tone: 'wait', text: 'Packing' }
    case 'CLOSED':
      return { tone: 'wait', text: 'Closed · waiting for release' }
    case 'RELEASED':
      return { tone: 'ok', text: 'Released' }
    case 'WITHDRAWN':
      return { tone: 'bad', text: 'Withdrawn' }
    case 'DESTROYED':
      return { tone: 'idle', text: 'Destroyed' }
    default:
      return { tone: 'idle', text: status }
  }
}

/** Lab status of a batch or pack lot (GET /units/:id/lab-status overall). */
export function labState(overall: string | null | undefined): { tone: Tone; text: string } {
  switch (overall) {
    case 'PASS':
      return { tone: 'ok', text: 'Pass' }
    case 'FAIL':
      return { tone: 'bad', text: 'Fail' }
    case 'INCOMPLETE':
      return { tone: 'bad', text: 'Incomplete' }
    case 'NOT_ACCEPTED':
      return { tone: 'bad', text: 'Not accepted' }
    default:
      return { tone: 'wait', text: 'Pending' }
  }
}

/** Why a batch cannot be packed (GET /packable-batches?code=), in plain words. */
const PACKABLE_REASON: Record<string, string> = {
  NOT_A_BATCH: 'Not a batch',
  ORDINARY_STREAM: 'Ordinary stock (red tie): never packed as brand',
  NOT_SINGLE_FARMER: 'Not from one farmer',
  NOT_COMMITTED: 'Not committed yet',
  NO_RUN: 'Not made by a machine run',
  UPSTREAM_NOT_BALANCED: 'The machine run that made it is not balanced',
  NO_QC_SAMPLE: 'No sealed QC sample',
  NO_KILOS_LEFT: 'No kilos left (already allocated)',
  LAB_FAIL: 'Lab fail',
  STATUS_CONSUMED: 'Used up',
  STATUS_VOID: 'Void',
  STATUS_DESTROYED: 'Destroyed',
}
export function packableReason(code: string): string {
  if (code.startsWith('ON_HOLD')) {
    const why = code.split(':').slice(1).join(':')
    return `On hold${why ? `: ${why.replace(/_/g, ' ').toLowerCase()}` : ''}. Cannot be packed until the hold is lifted.`
  }
  return PACKABLE_REASON[code] ?? code.replace(/_/g, ' ').toLowerCase()
}

/** Label version status in the boards' words. */
export function labelState(status: string, replaced: boolean): { tone: Tone; text: string } {
  if (status === 'APPROVED') return { tone: 'ok', text: 'Approved' }
  if (status === 'DRAFT') return { tone: 'wait', text: 'Waiting for approval' }
  if (status === 'RETIRED') return { tone: 'idle', text: replaced ? 'Replaced' : 'Withdrawn' }
  return { tone: 'idle', text: status }
}

/** A label's claim must give the LOQ and never say "residue-free" (F7.4 val 7). */
export function claimProblems(
  claim: string | null | undefined,
  maxLoq: string | null | undefined,
): string[] {
  const out: string[] = []
  const c = (claim ?? '').trim()
  if (!c) out.push('No claim text')
  if (/residue[\s-]*free/i.test(c)) out.push("The claim says 'residue-free'")
  if (c && !/LOQ/i.test(c)) out.push('The claim does not give the LOQ')
  if (!maxLoq) out.push('No LOQ for the claim')
  return out
}

/** Pouches of each lot on a trip, and the trip total. */
export function tripPouches(items: ReadonlyArray<{ pouches: number }>): number {
  return items.reduce((s, i) => s + i.pouches, 0)
}
